import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FileText, Download, Phone, Mail, MapPin } from "lucide-react";
import Badge from "../../../../../components/elements/Badge";
import {
  applicationDetails,
  applicationDetailsUser,
} from "../../../../../services/applicationService";

const ApplicationDetails = () => {
  const { id } = useParams();

  const { data: details, isLoading } = useQuery({
    queryKey: ["application-details", id],
    queryFn: () => applicationDetails({ id }),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="p-6 text-gray-500">Loading application...</div>;
  }

  const data = details?.data;
  const isImageFile = (ext = "") =>
    ["jpg", "jpeg", "png", "webp", "gif"].includes(ext.toLowerCase());

  const forceDownload = async (url, filename) => {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "attachment";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className="p-6 space-y-6">
      {/* ===== Header ===== */}
      <div className="bg-white rounded-xl shadow p-6 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold">
            {data?.applicant?.firstName} {data?.applicant?.lastName}
          </h1>
          <p className="text-sm text-gray-500">
            Applied on {dayjs(data?.createdAt).format("DD MMM YYYY")}
          </p>
        </div>

        <Badge
          type={
            data?.status === "submitted"
              ? "info"
              : data?.status === "shortlisted"
                ? "warning"
                : data?.status === "hired"
                  ? "success"
                  : "danger"
          }
          label={data?.status}
        />
      </div>

      {/* ===== Contact & Address ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact */}
        <div className="bg-white rounded-xl shadow p-6 space-y-3">
          <h2 className="font-semibold text-lg">Contact Information</h2>

          <div className="flex items-center gap-2 text-sm">
            <Mail size={16} />
            {data?.applicant?.email}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Phone size={16} />
            {`${data?.applicant?.phone?.countryCode} ${data?.applicant?.phone?.number}`}
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-xl shadow p-6 space-y-2">
          <h2 className="font-semibold text-lg">Address</h2>

          <div className="flex gap-2 text-sm text-gray-700">
            <MapPin size={16} />
            <div>
              <p>{data?.address?.address1}</p>
              <p>
                {data?.address?.city}, {data?.address?.state}{" "}
                {data?.address?.zipcode}
              </p>
              <p>{data?.address?.country}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Education & Experience ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="font-semibold text-lg mb-3">Education</h2>
          <ul className="list-disc ml-5 text-sm space-y-1">
            {data?.education?.map((edu, i) => (
              <li key={i}>{edu}</li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="font-semibold text-lg mb-3">Experience</h2>
          <ul className="list-disc ml-5 text-sm space-y-1">
            {data?.experience?.map((exp, i) => (
              <li key={i}>{exp}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* ===== Resume / Attachments ===== */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="font-semibold text-lg mb-4">Resume / Attachments</h2>

        <div className="flex flex-wrap gap-4">
          {data?.attachments?.map((file) => {
            const isImage = isImageFile(file.extension);

            return (
              <div
                key={file._id}
                className="border rounded-lg p-3 w-56 flex flex-col gap-3"
              >
                {isImage ? (
                  <img
                    src={file.url}
                    alt={file.filename}
                    className="w-full h-40 object-cover rounded"
                  />
                ) : (
                  <div className="h-40 flex flex-col items-center justify-center bg-gray-100 rounded">
                    <FileText size={40} className="text-red-500" />
                    <span className="text-xs mt-2 uppercase">
                      {file.extension}
                    </span>
                  </div>
                )}

                <div className="text-xs truncate">{file.filename}</div>

                <button
                  onClick={() => forceDownload(file.url, file.filename)}
                  className="flex items-center justify-center gap-2 text-sm text-white bg-primary rounded-md py-2 hover:bg-primary/90"
                >
                  <Download size={16} />
                  Download
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Message ===== */}
      {data?.message && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="font-semibold text-lg mb-2">Applicant Message</h2>
          <p className="text-sm text-gray-700">{data.message}</p>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetails;
